import { useRef, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Typography,
    CardFooter,
} from "@material-tailwind/react";
import dayjs from "dayjs";
import ContentWrapper from "components/ui/content-wrapper";
import useSWR from "swr";
import * as Yup from "yup";
import { fetcherFileUpload, fetcherFull, fetcherSimple } from "utils/api";
import { API_SERVER } from "utils/config";
import { toast } from "react-toastify";
import Image from "components/image";
import UploadPicture from "assets/img/uploadprofilepic.png";
import MaterialIcon from "components/material-icon";
import UpdatePasswordDialog from "components/dialog/update-password-dialog";
import ManageUserDialog from "components/dialog/manage-user-dialog";
import { sanitiseInput, sanitiseText } from "utils/utils.jsx";

export default function Account() {
    //AccordionControls
    const [showUpdatePasswordDialog, setShowUpdatePasswordDialog] =
        useState(false);
    const [showManageUserDialog, setShowManageUserDialog] = useState(false);
    const [profilePicDeletable, setProfilePicDeletable] = useState(true);
    // timestamp value function with dayjs
    const [timestamp, setTimestamp] = useState(dayjs());

    // Refs
    const inputLogoFileRef = useRef(null);

    const {
        data: userData,
        error: userDataError,
        mutate: userDataMutate,
        isValidating: userDataIsValidating,
    } = useSWR([`/v2/user/@me`, "GET"], ([path, method]) =>
        fetcherSimple(path, method),
    );

    const imageUrl = API_SERVER + `/v2/user/@me/picture`;

    const validationSchemaUser = Yup.object().shape({
        name: Yup.string().required(),
        alt_email: Yup.string().optional(),
        phone: Yup.string().optional(),
        address_1: Yup.string().optional(),
        address_2: Yup.string().optional(),
        postcode: Yup.string().optional(),
        country: Yup.string().optional(),
        job_role: Yup.string().optional(),
    });

    const validationSchemaPassword = Yup.object().shape({
        password: Yup.string().required("New Password is Required"),
        confirm_password: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords don't match")
            .required("Confirm Password is required"),
    });

    const handleLogoUploadClick = () => {
        inputLogoFileRef.current?.click();
    };

    const profilePicUploadHandler = async (event) => {
        const csvSubmitToast = toast.loading("Saving Image...");
        if (!event.target.files) {
            toast.error("No file uploaded!");
            return;
        }
        const selectedFile = event.target.files[0];
        if (!selectedFile.type.includes("image/")) {
            toast.error(
                "Only Image Files are supported. Please ensure that the file you are loading is not excessively large and has the correct extension and name.",
            );
            return;
        }
        let imageResponse = undefined;
        try {
            imageResponse = await fetcherFileUpload(
                `/v2/user/@me/picture`,
                "POST",
                "picture",
                selectedFile,
            );
        } catch {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            toast.update(csvSubmitToast, {
                render: `Error uploading Image, ${serverError}.`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
        if (imageResponse) {
            toast.update(csvSubmitToast, {
                render: "Profile Picture Uploaded",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            setTimestamp(dayjs());
            setProfilePicDeletable(true);
        } else {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            toast.update(csvSubmitToast, {
                render: `Error uploading Image, ${serverError}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            console.log(imageResponse.errors);
        }
    };

    const handleDeleteProfilePic = async () => {
        const deleteResponse = await fetcherSimple(
            `/v2/user/@me/picture`,
            "DELETE",
        );
        if (deleteResponse && deleteResponse.deleted) {
            toast.success("Profile Picture Deleted!");
            setProfilePicDeletable(false);
            setTimestamp(dayjs());
        } else if (deleteResponse && deleteResponse.error) {
            toast.error(`Error deleting picture: ${deleteResponse.message}`);
        } else {
            toast.error("Error deleting picture!");
        }
    };

    const handleUserSubmit = async (values) => {
        setShowManageUserDialog(false);
        let sanitary_values = {};
        for (const property in values) {
            if (values[property]) {
                sanitary_values[property] = values[property];
            }
        }

        // Sanitize name field before submission
        if (sanitary_values.name) {
            sanitary_values.name = sanitiseText(sanitary_values.name);
        }

        const userSubmitToast = toast.loading("Saving User...");
        let response = undefined;
        let serverError = undefined;
        try {
            response = await fetcherFull(
                `/v2/user/@me`,
                "PUT",
                sanitary_values,
            );
        } catch {
            serverError = response || "Unknown issue";
        }
        if (response?.success && response?.success === false) {
            toast.update(userSubmitToast, {
                render: `Error updating User, ${
                    response.error || "unknown error"
                }`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            console.log(response.error);
        } else if (serverError) {
            toast.update(userSubmitToast, {
                render: `Error updating User, ${
                    serverError || "unknown error"
                }`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        } else {
            toast.update(userSubmitToast, {
                render: "User Updated",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            userDataMutate(`/v2/user/@me`);
        }
    };

    const handlePasswordSubmit = async (values) => {
        delete values.confirm_password;
        const sanitisedValues = {
            ...values,
            ...(values.name
                ? { name: sanitiseInput(values.name) }
                : {}),
            ...(values.password
                ? { password: values.password }
                : {}),
        };
        const userSubmitToast = toast.loading("Saving Password...");
        let response = undefined;
        let serverError = undefined;
        try {
            response = await fetcherFull(`/v2/user/@me`, "PUT", sanitisedValues);
        } catch {
            serverError = response || "Unknown issue";
        }
        if (!sanitisedValues.password || sanitisedValues.password === "") {
            delete sanitisedValues.password;
        }
        if (response?.success && response?.success === false) {
            toast.update(userSubmitToast, {
                render: `Error updating Password, ${
                    response.error || "unknown error"
                }`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            console.log(response.error);
        } else if (serverError) {
            toast.update(userSubmitToast, {
                render: `Error updating Password, ${
                    serverError || "unknown error"
                }`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        } else {
            toast.update(userSubmitToast, {
                render: "Password Updated",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            userDataMutate(`/v2/user/@me`);
        }
    };

    return (
        <>
            <ContentWrapper>
                <div>
                    <div className="mt-4 mx-auto w-fit">
                        <Card
                            className="card"
                            variant="gradient"
                            shadow={false}
                        >
                            <CardHeader
                                className="card-inner-header mx-0"
                                color="transparent"
                                floated={true}
                                shadow={false}
                            >
                                <Typography variant="h3">
                                    Account Details
                                </Typography>
                            </CardHeader>
                            <CardBody className="card-inner my-2">
                                <div className="flex flex-row">
                                    <div className="basis-2/3 card-inner-body">
                                        <Typography variant="h4">
                                            Account Holder
                                        </Typography>
                                        <Typography variant="paragraph">
                                            <MaterialIcon name="person" />{" "}
                                            {userData?.name || "N/A"}
                                            <br />
                                            <MaterialIcon name="alternate_email" />{" "}
                                            {userData?.alt_email || "N/A"}
                                            <br />
                                            <MaterialIcon name="call" />{" "}
                                            {userData?.phone || "N/A"}
                                            <br />
                                        </Typography>
                                    </div>
                                    <div className="basis-1/3 w-full p-2 rounded-2xl ml-4 static">
                                        <Image
                                            alt="Profile PictureORGANISATION —"
                                            key={timestamp}
                                            src={imageUrl}
                                            className="mt-6 rounded-lg border border-black"
                                            onError={({ currentTarget }) => {
                                                currentTarget.onerror = null; // prevents looping
                                                currentTarget.src = `${UploadPicture}`;
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="card-inner-body my-4">
                                    <Typography variant="h4">
                                        Organisation
                                    </Typography>
                                    <Typography variant="paragraph">
                                        <MaterialIcon name="store" />{" "}
                                        {userData?.organisation || "N/A"}
                                        <br />
                                        <MaterialIcon name="badge" />{" "}
                                        {userData?.job_role || "N/A"}
                                        <br />
                                    </Typography>
                                </div>
                                <div className="card-inner-body my-2">
                                    <Typography variant="h4">
                                        Address
                                    </Typography>
                                    <Typography variant="paragraph">
                                        <MaterialIcon name="contact_mail" />{" "}
                                        {userData?.address_1 || "N/A"}
                                        <br />
                                        <MaterialIcon name="contact_mail" />{" "}
                                        {userData?.address_2 || "N/A"}
                                        <br />
                                        <MaterialIcon name="contact_mail" />{" "}
                                        {userData?.postcode || "N/A"}
                                        <br />
                                        <MaterialIcon name="contact_mail" />{" "}
                                        {userData?.country || "N/A"}
                                        <br />
                                    </Typography>
                                </div>
                            </CardBody>
                            <CardFooter className="flex justify-end px-0">
                                <Button
                                    className="btn btn--secondary"
                                    onClick={() =>
                                        setShowManageUserDialog(true)
                                    }
                                >
                                    Update Details
                                </Button>
                                <Button
                                    className="btn btn--secondary ml-4"
                                    onClick={() =>
                                        setShowUpdatePasswordDialog(true)
                                    }
                                >
                                    Update Password
                                </Button>
                            </CardFooter>
                        </Card>
                    </div>
                    <div className="basis-1/3 rounded-xl min-h-[100px] mt-4"></div>
                </div>
            </ContentWrapper>
            <UpdatePasswordDialog
                handlePasswordSubmit={handlePasswordSubmit}
                validationSchemaPassword={validationSchemaPassword}
                showUpdatePasswordDialog={showUpdatePasswordDialog}
                setShowUpdatePasswordDialog={setShowUpdatePasswordDialog}
            />
            <ManageUserDialog
                handleUserSubmit={handleUserSubmit}
                validationSchemaUser={validationSchemaUser}
                showManageUserDialog={showManageUserDialog}
                setShowManageUserDialog={setShowManageUserDialog}
                handleLogoUploadClick={handleLogoUploadClick}
                profilePicDeletable={profilePicDeletable}
                handleDeleteProfilePic={handleDeleteProfilePic}
                profilePicUploadHandler={profilePicUploadHandler}
                inputLogoFileRef={inputLogoFileRef}
                timestamp={timestamp}
                imageUrl={imageUrl}
                UploadPicture={UploadPicture}
                setProfilePicDeletable={setProfilePicDeletable}
                userData={userData}
            />
        </>
    );
}
